/**
 * Dropped-in art, found and swapped at render time.
 *
 * The game draws every leader as procedural vector — that is the guarantee that
 * there is always a face. This module is the door for *better* faces: if a
 * painted portrait exists at `art/leaders/<leaderId>.png` (the contract in
 * ARTBRIEF.md), it replaces the vector the moment it loads, and a missing or
 * broken file costs nothing but one failed probe, remembered.
 *
 * Probes go through `new Image()` rather than an inline `onerror` attribute,
 * because the desktop shell's CSP forbids inline handlers — the same policy
 * that took the styles down once already. Results are cached per id for the
 * session: one probe per leader, however many times the rail re-renders.
 */

const probed = new Map<string, HTMLImageElement | 'missing'>();

function probe(id: string, done: (img: HTMLImageElement | 'missing') => void): void {
  const hit = probed.get(id);
  if (hit) {
    done(hit);
    return;
  }
  const img = new Image();
  img.onload = () => {
    probed.set(id, img);
    done(img);
  };
  img.onerror = () => {
    probed.set(id, 'missing');
    done('missing');
  };
  img.src = `art/leaders/${id}.png`;
}

/**
 * Swap every vector portrait under `root` whose painted file exists.
 *
 * Safe to call after any render that produced portraits; elements that have
 * gone by the time a probe answers are skipped by the `isConnected` check.
 */
export function hydrateLeaderArt(root: ParentNode): void {
  for (const svg of root.querySelectorAll<SVGSVGElement>('svg[data-lp]')) {
    const id = svg.dataset.lp as string;
    probe(id, (img) => {
      if (img === 'missing' || !svg.isConnected) return;
      const el = img.cloneNode() as HTMLImageElement;
      el.width = Number(svg.getAttribute('width'));
      el.height = Number(svg.getAttribute('height'));
      el.style.display = 'block';
      el.style.objectFit = 'cover';
      if (svg.dataset.lpmuted === '1') el.style.filter = 'saturate(0.42) brightness(0.94)';
      el.alt = svg.getAttribute('aria-label') ?? id;
      svg.replaceWith(el);
    });
  }
}
